import React, { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useSocket } from '../context/SocketContext';
import SeatModal from '../components/SeatModal';
import CreateTableModal from '../components/CreateTableModal';
import TileGraphic from '../components/TileGraphic';
import './Dashboard.css';

const HAND_RANKINGS = [
  {
    rank: 1, name: 'Pung + Pair', odds: '0.8%',
    desc: 'Three of a kind + any pair',
    accent: '#c9a227', bg: 'rgba(201,162,39,0.07)',
    tiles: [
      { suit: 'B', value: 7 }, { suit: 'B', value: 7 }, { suit: 'B', value: 7 },
      null,
      { suit: 'O', value: 9 }, { suit: 'O', value: 9 },
    ],
  },
  {
    rank: 2, name: 'Chow + Pair', odds: '8%',
    desc: 'Three consecutive same-suit tiles + any pair',
    accent: '#1db974', bg: 'rgba(29,185,116,0.06)',
    tiles: [
      { suit: 'O', value: 3 }, { suit: 'O', value: 4 }, { suit: 'O', value: 5 },
      null,
      { suit: 'C', value: 8 }, { suit: 'C', value: 8 },
    ],
  },
  {
    rank: 3, name: 'Pair only', odds: '60%',
    desc: 'Best pair from your 5 tiles',
    accent: '#3b82f6', bg: 'rgba(59,130,246,0.06)',
    tiles: [
      { suit: 'C', value: 6 }, { suit: 'C', value: 6 },
    ],
  },
  {
    rank: 4, name: 'High Tile', odds: '31%',
    desc: 'No meld or pair — highest tile wins',
    accent: '#6b7280', bg: 'transparent',
    tiles: [
      { suit: 'B', value: 9 },
    ],
  },
];

function TableCard({ table, mySession, myWaitlist, onOpen, onObserve }) {
  const takenCount = table.seats.filter(s => s.player).length;
  const isFull     = takenCount === table.max;
  const fillPct    = (takenCount / table.max) * 100;

  const isSeated    = mySession !== undefined;
  const isWaitlisted = myWaitlist !== undefined;

  let btnLabel = isFull ? 'Join Waitlist' : 'Take a Seat';
  if (isSeated)    btnLabel = 'Manage Seat';
  if (isWaitlisted) btnLabel = `Waitlisted #${myWaitlist}`;

  return (
    <div className={`table-card ${isFull && !isSeated ? 'full' : ''} ${isSeated ? 'active' : ''}`}>
      <div className="table-card-top">
        <span className="table-name">{table.name}</span>
        <span className={`type-badge ${{ 'No-Limit': 'nl', 'Pot-Limit': 'pl', 'Limit': 'limit', 'Spread-Limit': 'sl' }[table.type] ?? 'pl'}`}>{table.type}</span>
      </div>
      <div className="table-stakes">
        {table.stakes}
        {table.config.handCap != null && (
          <span className="cap-badge">
            Cap ${table.config.handCap >= 1000 ? `${(table.config.handCap/1000).toFixed(0)}k` : table.config.handCap}/hand
          </span>
        )}
      </div>
      <div className="seat-bar-wrap">
        <div className="seat-bar">
          <div className="seat-bar-fill" style={{ width: `${fillPct}%` }} />
        </div>
        <span className="seat-count">{takenCount}/{table.max} seated</span>
      </div>
      {takenCount > 0 && (
        <div className="player-list">
          {table.seats.filter(s => s.player).map(s => (
            <span key={s.index} className={`player-chip ${s.player.isBot ? 'is-bot' : ''}`}>
              {s.player.name}
              {s.player.isBot && <span className="bot-tag">bot</span>}
            </span>
          ))}
        </div>
      )}
      <div className="table-card-actions">
        <button className="join-btn" onClick={onOpen}>{btnLabel}</button>
        <button className="observe-btn" onClick={onObserve}>Observe</button>
      </div>
    </div>
  );
}

export default function Dashboard() {
  const { user, wallet }   = useAuth();
  const { tables, sessions, waitlists, createTable } = useSocket();
  const navigate = useNavigate();
  const [modalTableId,    setModalTableId]    = useState(null);
  const [showCreateModal, setShowCreateModal] = useState(false);

  // Tables in play open in their own browser window, which is how a poker
  // client multi-tables: real windows you can move between monitors, size
  // independently and leave behind your browser while you do something else.
  //
  // Windows are kept in a ref rather than state because nothing renders from
  // them — they are outside this document.
  const tableWindows = useRef(new Map());

  const activeSessions  = Object.entries(sessions).map(([id, seat]) => ({ table: tables.find(t => t.id === id), seat }));
  const activeWaitlists = Object.entries(waitlists).map(([id, pos]) => ({ table: tables.find(t => t.id === id), pos }));
  const hasActivity = activeSessions.length > 0 || activeWaitlists.length > 0;

  const modalTable = modalTableId !== null ? tables.find(t => t.id === modalTableId) : null;

  /* Open one table in its own window, or focus the window it already has.
   *
   * Named per table, so clicking a table twice raises the window it already
   * owns instead of opening a second copy of the same game. Called straight
   * from a click so the browser does not treat it as an unsolicited popup.
   *
   * The new window shares this browser's origin and storage, so it is already
   * signed in and carries the same client id — the one-device rule sees the
   * same device and leaves both windows connected. */
  const openTable = (id) => {
    const name = `triadha-table-${id}`;
    const existing = tableWindows.current.get(id);
    if (existing && !existing.closed) {
      existing.focus();
      return;
    }
    const w = window.open(
      `${process.env.REACT_APP_BASE_PATH || ''}/table/${id}`,
      name,
      'width=1100,height=760,noopener=no',
    );
    if (!w) {
      // Blocked. Fall back to the in-page route rather than doing nothing.
      navigate(`/table/${id}`);
      return;
    }
    tableWindows.current.set(id, w);
    w.focus();
  };

  return (
    <div className="dashboard">

      {/* ── Sticky header: welcome + sessions + rankings ── */}
      <div className="dashboard-sticky-top">
        <section className="welcome-bar">
          <div className="welcome-inner">
            <div>
              <p className="welcome-label">Welcome back</p>
              <h1 className="welcome-name">{user?.name}</h1>
            </div>
            <div className="welcome-right">
              <div className="wallet-display">
                <span className="wallet-display-label">Wallet</span>
                <span className="wallet-display-amount">${wallet.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
              </div>
              <button className="create-table-btn" onClick={() => setShowCreateModal(true)}>+ Create Table</button>
            </div>
          </div>
        </section>

        {hasActivity && (
          <section className="sessions-bar">
            <div className="sessions-inner">
              <span className="sessions-label">Your Tables</span>
              <div className="sessions-list">
                {activeSessions.map(({ table, seat }) => (
                  <button key={table.id} className="session-chip seated" onClick={() => openTable(table.id)}>
                    <span className="session-chip-name">{table.name}</span>
                    <span className="session-chip-detail">Seat {seat + 1}</span>
                  </button>
                ))}
                {activeWaitlists.map(({ table, pos }) => (
                  <button key={table.id} className="session-chip waiting" onClick={() => setModalTableId(table.id)}>
                    <span className="session-chip-name">{table.name}</span>
                    <span className="session-chip-detail">#{pos} waiting</span>
                  </button>
                ))}
              </div>
            </div>
          </section>
        )}

        {/* Horizontal hand rankings strip */}
        <div className="rankings-bar">
          <span className="rankings-bar-label">Rankings</span>
          {HAND_RANKINGS.map(h => (
            <div className="rankings-bar-card" key={h.rank}>
              <span className="rb-pos">#{h.rank}</span>
              <span className="rb-name">{h.name}</span>
              <span className="rb-odds">{h.odds}</span>
            </div>
          ))}
        </div>
      </div>

      {/* ── Scrollable lobby ── */}
      <div className="dashboard-body">

        {/* Full graphic hand rankings */}
        <section className="lobby-section">
          <h2 className="section-title">Hand Rankings</h2>
          <div className="rankings-grid">
            {HAND_RANKINGS.map(h => (
              <div className="ranking-card" key={h.rank}
                style={{ borderColor: h.accent + 'aa', background: h.bg }}>
                <div className="ranking-top">
                  <span className="ranking-pos" style={{ color: h.accent }}>#{h.rank}</span>
                  <span className="ranking-name">{h.name}</span>
                  <span className="ranking-odds" style={{ color: h.accent }}>{h.odds}</span>
                </div>
                <div className="ranking-example-tiles">
                  {h.tiles.map((t, i) => (
                    t === null
                      ? <span key={i} className="ranking-tile-sep">+</span>
                      : <div key={i} className="ranking-tile-mini"><TileGraphic tile={t} /></div>
                  ))}
                </div>
                <p className="ranking-desc">{h.desc}</p>
              </div>
            ))}
          </div>
        </section>

        {/* Active tables */}
        <section className="lobby-section">
          <h2 className="section-title">
            Active Tables
            <span className="section-count">{tables.filter(t => t.seats.filter(s => s.player).length < t.max).length} open</span>
          </h2>
          <div className="tables-grid">
            {tables.map(t => (
              <TableCard
                key={t.id}
                table={t}
                mySession={sessions[t.id]}
                myWaitlist={waitlists[t.id]}
                onOpen={() => setModalTableId(t.id)}
                onObserve={() => openTable(t.id)}
              />
            ))}
          </div>
        </section>
      </div>

      {modalTable && (
        <SeatModal
          table={modalTable}
          onClose={() => setModalTableId(null)}
          onJoined={(tableId) => { setModalTableId(null); openTable(tableId); }}
        />
      )}

      {showCreateModal && (
        <CreateTableModal
          onClose={() => setShowCreateModal(false)}
          onCreate={(config) => {
            createTable(config);
            setShowCreateModal(false);
            navigate('/dashboard');
          }}
        />
      )}
    </div>
  );
}
